import {
  compareDatetimeOriginTransformations,
  DatasetPropertyResponse,
  DatasetPropertyType,
  DatasetResponse,
  DatetimeOriginTransformation,
  JoinStepConditionRequest,
  JoinStepConditionResponse,
  JoinType,
} from 'mapistry-shared';
import { DeepPartial } from 'utility-types';
import { TimeSeriesJoinType } from './types';

export function getTimeSeriesJoinType(
  leftDataset: DatasetResponse,
  rightDataset: DatasetResponse,
): TimeSeriesJoinType {
  const leftTimeSeriesProperty = leftDataset.schemaProperties.find(
    (p) => p.isTimeSeriesDate,
  );
  const rightTimeSeriesProperty = rightDataset.schemaProperties.find(
    (p) => p.isTimeSeriesDate,
  );

  if (
    leftTimeSeriesProperty &&
    leftTimeSeriesProperty.type === DatasetPropertyType.DATETIME
  ) {
    if (!rightTimeSeriesProperty) {
      return TimeSeriesJoinType.DATETIME_TO_NO_TIME_SERIES;
    }
    if (rightTimeSeriesProperty.type === DatasetPropertyType.DATETIME) {
      return TimeSeriesJoinType.DATETIME_TO_DATETIME;
    }
    if (rightTimeSeriesProperty.type === DatasetPropertyType.DATE_RANGE) {
      return TimeSeriesJoinType.DATETIME_TO_DATE_RANGE;
    }
  }

  if (
    leftTimeSeriesProperty &&
    leftTimeSeriesProperty.type === DatasetPropertyType.DATE_RANGE
  ) {
    if (!rightTimeSeriesProperty) {
      return TimeSeriesJoinType.DATE_RANGE_TO_NO_TIME_SERIES;
    }
    if (rightTimeSeriesProperty.type === DatasetPropertyType.DATETIME) {
      return TimeSeriesJoinType.DATE_RANGE_TO_DATETIME;
    }
    if (rightTimeSeriesProperty.type === DatasetPropertyType.DATE_RANGE) {
      return TimeSeriesJoinType.DATE_RANGE_TO_DATE_RANGE;
    }
  }

  return TimeSeriesJoinType.NO_TIME_SERIES_TO_NO_TIME_SERIES;
}

export function getTimeSeriesDateRangeProperties(dataset: DatasetResponse) {
  const timeSeriesProperty = dataset.schemaProperties.find(
    (p) => p.isTimeSeriesDate,
  );
  if (!timeSeriesProperty) {
    return [];
  }
  const parentTimeSeriesProperty =
    timeSeriesProperty.type === DatasetPropertyType.DATETIME
      ? timeSeriesProperty
      : dataset.schemaProperties.find(
          (p) =>
            p.id === timeSeriesProperty.parentPropertyId && !p.parentPropertyId,
        );
  if (!parentTimeSeriesProperty) {
    return [];
  }
  return dataset.schemaProperties.filter(
    (p) =>
      p.type === DatasetPropertyType.DATE_RANGE &&
      p.parentPropertyId === parentTimeSeriesProperty.id &&
      p.parentPropertyIdChain.length === 1,
  );
}

export function getTimeSeriesDateRangeByTransformation(
  dataset: DatasetResponse,
  transformation: DatetimeOriginTransformation,
) {
  const timeSeriesDateRanges = getTimeSeriesDateRangeProperties(dataset);
  return timeSeriesDateRanges.find(
    (p) => p.dateTransformation === transformation,
  );
}

export function isDateIntervalCondition(
  condition: JoinStepConditionResponse,
  inputTimeSeriesDateRanges: DatasetPropertyResponse[],
  joinToTimeSeriesDateRanges: DatasetPropertyResponse[],
): boolean {
  if (!inputTimeSeriesDateRanges.length || !joinToTimeSeriesDateRanges.length) {
    return false;
  }
  const inputDateRangePropertyIds = inputTimeSeriesDateRanges.map((p) => p.id);
  const joinToDateRangePropertyIds = joinToTimeSeriesDateRanges.map(
    (p) => p.id,
  );
  return (
    inputDateRangePropertyIds.includes(condition.leftPropertyId) &&
    joinToDateRangePropertyIds.includes(condition.rightPropertyId)
  );
}

export function doesNeedDateIntervalCondition(
  datasetDataType: TimeSeriesJoinType,
) {
  switch (datasetDataType) {
    case TimeSeriesJoinType.DATETIME_TO_DATETIME:
    case TimeSeriesJoinType.DATETIME_TO_DATE_RANGE:
    case TimeSeriesJoinType.DATE_RANGE_TO_DATETIME:
    case TimeSeriesJoinType.DATE_RANGE_TO_DATE_RANGE:
      return true;
    case TimeSeriesJoinType.DATETIME_TO_NO_TIME_SERIES:
    case TimeSeriesJoinType.DATE_RANGE_TO_NO_TIME_SERIES:
    case TimeSeriesJoinType.NO_TIME_SERIES_TO_NO_TIME_SERIES:
      return false;
    default: {
      const exhaustiveCheck: never = datasetDataType;
      throw new Error(
        `Couldn't identify whether connecting time series datasets or not - ${exhaustiveCheck}`,
      );
    }
  }
}

export function isDateConditionDisabled(datasetDataType: TimeSeriesJoinType) {
  switch (datasetDataType) {
    case TimeSeriesJoinType.DATETIME_TO_DATE_RANGE:
    case TimeSeriesJoinType.DATE_RANGE_TO_DATETIME:
    case TimeSeriesJoinType.DATE_RANGE_TO_DATE_RANGE:
      return true;
    case TimeSeriesJoinType.DATETIME_TO_DATETIME:
    case TimeSeriesJoinType.DATETIME_TO_NO_TIME_SERIES:
    case TimeSeriesJoinType.DATE_RANGE_TO_NO_TIME_SERIES:
    case TimeSeriesJoinType.NO_TIME_SERIES_TO_NO_TIME_SERIES:
      return false;
    default: {
      const exhaustiveCheck: never = datasetDataType;
      throw new Error(
        `Couldn't identify whether connecting time series datasets or not - ${exhaustiveCheck}`,
      );
    }
  }
}

export function getDefaultDateInterval(
  leftDataset: DatasetResponse,
  rightDataset: DatasetResponse,
): DatetimeOriginTransformation | undefined {
  // a date interval should only be selected if both datasets have a time series date property
  if (
    !doesNeedDateIntervalCondition(
      getTimeSeriesJoinType(leftDataset, rightDataset),
    )
  ) {
    return undefined;
  }

  const leftInterval = leftDataset.schemaProperties.find(
    (p) => p.isTimeSeriesDate,
  )?.dateTransformation;
  const rightInterval = rightDataset.schemaProperties.find(
    (p) => p.isTimeSeriesDate,
  )?.dateTransformation;
  if (leftInterval && rightInterval) {
    // Selecting the largest interval of the two, e.g. year if we are joining monthly and yearly aggregated datasets.
    return compareDatetimeOriginTransformations(leftInterval, rightInterval) > 0
      ? leftInterval
      : rightInterval;
  }
  return leftInterval || rightInterval || undefined;
}

export function getDefaultJoinType(): JoinType | undefined {
  return undefined;
}

export function areAllRegularConditionsOptional(
  datasetDataType: TimeSeriesJoinType,
) {
  return doesNeedDateIntervalCondition(datasetDataType);
}

export function getDefaultConditions(
  datasetDataType: TimeSeriesJoinType,
): DeepPartial<JoinStepConditionRequest[]> {
  if (areAllRegularConditionsOptional(datasetDataType)) {
    return [];
  }
  return [{ leftPropertyId: undefined, rightPropertyId: undefined }];
}
